/**
 * Dialog para criar Ação Rápida (Regra de Roteamento Manual)
 * 
 * Versão simplificada do CreateRuleDialog com condition_type='manual_execution' fixo.
 * Usado na seção Ações Rápidas do chat para criar regras específicas do agente.
 * 
 * @version 1.0.0
 * @date 2025-12-15
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MousePointerClick } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RoutingActionBuilder } from './RoutingActionBuilder';
import { RoutingActionItem } from './types';
import { RuleVisibilitySelector, RuleVisibility } from './RuleVisibilitySelector';
import { RoutingCategorySelect } from './categories/RoutingCategorySelect';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('CreateManualRuleDialog');

interface CreateManualRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * Dialog especializado para criar ações rápidas (regras manuais)
 * Com condição manual_execution fixada e interface simplificada
 */
export function CreateManualRuleDialog({ 
  open, 
  onOpenChange, 
  onSuccess 
}: CreateManualRuleDialogProps) {
  const { currentTenant, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Campos do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actions, setActions] = useState<RoutingActionItem[]>([]);
  const [stopOnMatch, setStopOnMatch] = useState(true);
  // Estados de visibilidade
  const [visibility, setVisibility] = useState<RuleVisibility>('private');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // Categoria (opcional)
  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Config de condição FIXA como manual_execution
  const conditionConfig = {
    logic_operator: 'and' as const,
    conditions: [{
      id: crypto.randomUUID(),
      type: 'manual_execution' as const,
      value: {},
    }],
  };

  // Reset form para estado inicial
  const resetForm = () => {
    setName('');
    setDescription('');
    setActions([]);
    setStopOnMatch(true);
    setVisibility('private');
    setSelectedUserIds([]);
    setCategoryId(null);
  };

  // Criar regra
  const handleSubmit = async () => {
    if (!currentTenant?.id) return;

    // Validações básicas
    if (!name.trim()) {
      showToast.error('Nome é obrigatório');
      return;
    }

    // Validação de ações
    if (actions.length === 0) {
      showToast.error('Adicione pelo menos uma ação');
      return;
    }

    setLoading(true);

    try {
      const firstAction = actions[0];

      // Cria regra manual + actions + allowed users atomicamente via RPC
      const { error: ruleError } = await supabase.rpc('create_routing_rule', {
        p_payload: {
          name: name.trim(),
          description: description.trim() || null,
          condition_type: 'manual_execution',
          condition_value: conditionConfig as unknown as Record<string, unknown>,
          action_type: firstAction.action_type,
          action_value: firstAction.action_value as Record<string, unknown>,
          stop_on_match: stopOnMatch,
          visibility: visibility,
          category_id: categoryId,
        } as any,
        p_actions: actions.map((action, index) => ({
          action_type: action.action_type,
          action_value: action.action_value,
          sort_order: index,
          delay_seconds: action.delay_seconds || 0,
        })) as any,
        p_allowed_user_ids: visibility === 'shared' && selectedUserIds.length > 0
          ? selectedUserIds
          : null,
        p_tenant_hint: currentTenant.id,
      });

      if (ruleError) throw ruleError;

      logger.info('Ação rápida criada com sucesso', { name, visibility });
      showToast.success('Ação rápida criada com sucesso');
      resetForm();
      onSuccess();
    } catch (error) {
      logger.error('Erro ao criar ação rápida', error);
      showToast.error('Erro ao criar ação rápida');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Nova Ação Rápida</DialogTitle>
          <DialogDescription>
            Crie uma sequência de ações que você poderá executar rapidamente durante conversas.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-6 py-4">
            {/* Nome e Descrição */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Ação *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Enviar proposta comercial"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrição opcional da ação..."
                  rows={2}
                />
              </div>
            </div>

            {/* Bloco visual de Execução Manual (read-only) */}
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                  <MousePointerClick className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Execução Manual
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Esta ação será executada quando você clicar no botão durante uma conversa.
                  </p>
                </div>
              </div>
            </div>

            {/* Visibilidade */}
            <RuleVisibilitySelector
              visibility={visibility}
              onVisibilityChange={setVisibility}
              selectedUserIds={selectedUserIds}
              onSelectedUserIdsChange={setSelectedUserIds}
            />

            {/* Categoria */}
            <div className="p-4 border rounded-lg">
              <RoutingCategorySelect value={categoryId} onChange={setCategoryId} />
            </div>


            {/* Sequência de Ações */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Sequência de Ações</h3>
              <RoutingActionBuilder
                items={actions}
                onItemsChange={setActions}
                selectedInboxIds={[]}
              />
            </div>

            {/* Opções */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="stop-on-match">Parar ao executar</Label>
                <p className="text-sm text-muted-foreground">
                  Se ativado, não executa outras regras automáticas após esta ação
                </p>
              </div>
              <Switch
                id="stop-on-match"
                checked={stopOnMatch}
                onCheckedChange={setStopOnMatch}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 shrink-0 border-t mt-auto">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Criando...' : 'Criar Ação Rápida'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
